import { getOperationAST } from 'graphql';
import gql from 'graphql-tag';
import { extractFiles } from 'extract-files';
import { v4 as uuidv4 } from 'uuid'; // or use your own UID generator

function cleanBodyAndFindFiles(body: object): {
  clone: object;
  files: Map<any, any>;
} {
  return extractFiles(body) as { clone: object; files: Map<any, any> };
}

const flashHandlers = new Map<string, (flash: unknown) => void>();
let currentRequestId: string | null = null;

/** Set current request id so the fetcher can match flash responses to the right handler. */
export function setCurrentRequestId(id: string | null) {
  currentRequestId = id;
}

export function registerFlashHandler(
  requestId: string,
  handler: (flash: unknown) => void
) {
  flashHandlers.set(requestId, handler);
}

export function clearFlashHandler(requestId: string) {
  flashHandlers.delete(requestId);
}

// To be read inside `fetcher`
export function handleFlashIfPresent(flash: unknown) {
  if (flash && currentRequestId && flashHandlers.has(currentRequestId)) {
    flashHandlers.get(currentRequestId)?.(flash);
    clearFlashHandler(currentRequestId);
  }
}

export function withQueryFlash<TArgs extends any[], TResult>(
  hook: (...args: TArgs) => TResult,
  onFlash?: (flash: unknown) => void
): (...args: TArgs) => TResult {
  return (...args: TArgs): TResult => {
    const requestId = uuidv4();

    if (onFlash) {
      registerFlashHandler(requestId, (flash) => {
        onFlash(flash);
      });
    }

    currentRequestId = requestId;

    try {
      return hook(...args);
    } finally {
      // Optional: clean up if it's synchronous
      // otherwise `fetcher` should handle it via `clearFlashHandler`
    }
  };
}

export const fetcher = <TData, TVariables>(
  query: string,
  variables?: TVariables,
  options?: RequestInit['headers']
): (() => Promise<TData>) => {
  return async (_variables?: TVariables) => {
    const operation = gql(query);
    const ast = getOperationAST(operation);
    const operationName = ast?.name?.value;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options,
    };

    const body = { operationName, variables };
    const { clone, files } = cleanBodyAndFindFiles(body);

    let res: Response;
    const fetchUrl = `/api/graphql?operationName=${operationName}`;

    if (files.size) {
      delete (headers as any)['Content-Type'];
      const form = new FormData();
      form.append(
        'operations',
        JSON.stringify({
          query,
          operationName,
          variables: (clone as any).variables,
        })
      );

      const map: Record<number, string[]> = {};
      let i = 0;
      files.forEach((paths) => {
        map[++i] = paths;
      });
      form.append('map', JSON.stringify(map));

      i = 0;
      files.forEach((paths, file) => {
        form.append(`${++i}`, file);
      });

      res = await fetch(fetchUrl, {
        method: 'POST',
        headers,
        body: form,
      });
    } else {
      res = await fetch(fetchUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query,
          operationName,
          variables,
        }),
      });
    }

    const json = await res.json();

    if (json.flash && currentRequestId && flashHandlers.has(currentRequestId)) {
      flashHandlers.get(currentRequestId)?.(json.flash);
      clearFlashHandler(currentRequestId);
    }

    if (!res.ok) {
      const details =
        typeof json.details === 'string'
          ? json.details
          : typeof json.error === 'string'
            ? json.error
            : undefined;
      throw new Error(details ?? `Request failed (${res.status})`);
    }

    if (json.errors) {
      const { message } = json.errors[0] || {};
      //   toast.toast({
      //     variant: 'destructive',
      //     title: 'Something went wrong...',
      //     description: message || 'Please contact support.',
      //   });
      //   throw new Error(message || "Error…");
    }

    // React Query requires query functions to never return undefined.
    return json.data ?? null;
  };
};
